import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import BrandLogo from './BrandLogo';

export default function OAuthConsentPage() {
  const [searchParams] = useSearchParams();
  const pendingApprovalSetup = searchParams.get('pendingApproval') === '1';
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [legal, setLegal] = useState({ termsVersion: '', privacyVersion: '' });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [mobilePhone, setMobilePhone] = useState('');
  const [representedClubName, setRepresentedClubName] = useState('');
  const [supportContactConsent, setSupportContactConsent] = useState(false);

  useEffect(() => {
    let active = true;
    const profileRequest = pendingApprovalSetup ? authAPI.oauthSetupProfile() : authAPI.getProfile();
    Promise.all([profileRequest, authAPI.legalCurrent()])
      .then(([profileRes, legalRes]) => {
        if (!active) return;
        const profile = profileRes.data?.data || profileRes.data;
        const current = legalRes.data || {};
        setUser(profile);
        setMobilePhone(profile?.mobilePhone || '');
        setRepresentedClubName(profile?.representedClubName || '');
        setSupportContactConsent(Boolean(profile?.supportContactConsent));
        setLegal({
          termsVersion: current.termsVersion || 'current',
          privacyVersion: current.privacyVersion || 'current'
        });
        if (profile) {
          localStorage.setItem('user', JSON.stringify(profile));
        }
        if (profile?.legalAcceptance?.termsAcceptedAt && profile?.legalAcceptance?.privacyAcceptedAt) {
          window.location.replace(pendingApprovalSetup ? '/pending-approval' : (profile.role === 'parent' ? '/parent' : '/'));
          return;
        }
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError('Please sign in again to finish setup.');
        setLoading(false);
      });
    return () => { active = false; };
  }, [pendingApprovalSetup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!acceptedTerms || !acceptedPrivacy) {
      setError('You must accept the Terms of Service and Privacy Policy to continue.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        acceptedTerms: true,
        acceptedPrivacy: true,
        termsVersion: legal.termsVersion,
        privacyVersion: legal.privacyVersion,
        ...(user?.role !== 'parent' ? {
          mobilePhone,
          representedClubName,
          supportContactConsent,
        } : {}),
      };
      if (pendingApprovalSetup) {
        await authAPI.oauthSetupComplete(payload);
        window.location.replace('/pending-approval');
        return;
      }
      await authAPI.legalAccept(payload);
      const refreshed = await authAPI.getProfile();
      const nextUser = refreshed.data?.data || refreshed.data || user;
      if (nextUser) {
        localStorage.setItem('user', JSON.stringify(nextUser));
      }
      window.location.replace(nextUser?.role === 'parent' ? '/parent' : '/');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save your acceptance. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div style={styles.shell}>
        <div style={{ ...styles.card, textAlign: 'center' }}>
          <BrandLogo variant="icon" height={42} showBeta />
          <div style={{ marginTop: 14 }}>Loading your account...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.shell}>
      <div style={styles.card}>
        <BrandLogo height={42} showBeta style={{ marginBottom: 20 }} />
        <h1 style={styles.title}>Finish setting up your account</h1>
        <p style={styles.text}>
          Before you continue, please accept the Terms of Service and Privacy Policy.
        </p>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          {user?.role !== 'parent' && (
            <div style={styles.supportSection}>
              <label htmlFor="oauth-mobile-phone" style={styles.fieldLabel}>Mobile number (optional)</label>
              <input
                id="oauth-mobile-phone"
                type="tel"
                autoComplete="tel"
                maxLength={32}
                value={mobilePhone}
                onChange={(e) => setMobilePhone(e.target.value)}
                style={styles.input}
              />
              <label htmlFor="oauth-represented-club" style={styles.fieldLabel}>Club or organisation you represent (optional)</label>
              <input
                id="oauth-represented-club"
                type="text"
                maxLength={160}
                value={representedClubName}
                onChange={(e) => setRepresentedClubName(e.target.value)}
                style={styles.input}
              />
              <p style={styles.helpText}>
                Used only to help with onboarding. This does not create a club or affect club search or membership.
              </p>
              {mobilePhone.trim() && (
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={supportContactConsent}
                    onChange={(e) => setSupportContactConsent(e.target.checked)}
                    style={styles.checkbox}
                  />
                  EliteEdge may contact me by phone about completing account setup.
                </label>
              )}
            </div>
          )}
          <label style={styles.label}>
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              style={styles.checkbox}
            />
            I accept the <Link to="/terms-of-service">Terms of Service</Link>.
          </label>
          <label style={styles.label}>
            <input
              type="checkbox"
              checked={acceptedPrivacy}
              onChange={(e) => setAcceptedPrivacy(e.target.checked)}
              style={styles.checkbox}
            />
            I accept the <Link to="/privacy-policy">Privacy Policy</Link>.
          </label>

          <button type="submit" disabled={submitting} style={styles.button}>
            {submitting ? 'Saving...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  shell: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg-primary)',
    padding: '24px',
    fontFamily: 'Inter, sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: '460px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderRadius: '16px',
    padding: '32px',
  },
  badge: {
    color: 'var(--accent)',
    fontWeight: 700,
    fontSize: '20px',
    textAlign: 'center',
    marginBottom: '8px',
  },
  title: {
    color: 'var(--text-primary)',
    fontSize: '24px',
    textAlign: 'center',
    margin: '0 0 12px 0',
  },
  text: {
    color: 'var(--text-secondary)',
    textAlign: 'center',
    lineHeight: 1.5,
    margin: '0 0 20px 0',
  },
  error: {
    background: '#450a0a',
    border: '1px solid #dc2626',
    color: '#fca5a5',
    padding: '10px 14px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '13px',
  },
  supportSection: {
    marginBottom: '18px',
    paddingBottom: '4px',
    borderBottom: '1px solid var(--border-color)',
  },
  fieldLabel: {
    display: 'block',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '11px 12px',
    marginBottom: '14px',
    borderRadius: '8px',
    border: '1px solid var(--border-input)',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    fontSize: '14px',
  },
  helpText: {
    color: 'var(--text-dim)',
    fontSize: '11px',
    lineHeight: 1.45,
    margin: '-4px 0 14px',
  },
  label: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    lineHeight: 1.5,
    marginBottom: '14px',
  },
  checkbox: {
    marginTop: '3px',
  },
  button: {
    width: '100%',
    marginTop: '8px',
    padding: '12px',
    border: 'none',
    borderRadius: '8px',
    background: 'var(--accent)',
    color: 'var(--text-on-accent)',
    fontWeight: 700,
    cursor: 'pointer',
  },
};
